import {
    Modal,
    Form,
    Select,
    InputNumber,
    Spin,
    Button,
    notification,
    Input
} from "antd";
import React, { useEffect, useState } from "react";
import { getUserPlan, updatePlanCappedUsage } from "../../Services/subscription";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const { Option } = Select;

function PlanCappedModal({ isModalVisible, handleCancel, initialValues }) {
    const [form] = Form.useForm();
    const [userSubscriptionId, setUserSubscriptionId] = useState(0);
    const [userSubscriptionFeatureId, setUserSubscriptionFetureId] = useState(0);
    const [isLimitValueDisabled, setIsLimitValueDisabled] = useState(false);
    const queryClient = useQueryClient();

    const {
        data,
        isLoading,
    } = useQuery({
        queryKey: [`user-plan-${initialValues?.id}`],
        queryFn: () => getUserPlan(initialValues?.id),
        enabled: !!initialValues?.id && isModalVisible,
    });

    const cappedUsageFeature = data?.user_plan_features?.find(
        (feature) => feature.feature_slug === "capped_usage"
    );

    useEffect(() => {
        if (data && cappedUsageFeature) {
            setUserSubscriptionId(data.id);
            const limitValue =
                cappedUsageFeature.limit_reset === "never"
                    ? null
                    : Number(cappedUsageFeature.limit_value) || 15;

            form.setFieldsValue({
                limit_reset: cappedUsageFeature.limit_reset || "day",
                limit_value: limitValue,
                original_limit_value: limitValue, 
            });

            setIsLimitValueDisabled(cappedUsageFeature.limit_reset === "never");
            setUserSubscriptionFetureId(cappedUsageFeature.id);
        }

        if (isModalVisible && !data) {
            form.resetFields();
            setUserSubscriptionId(0);
            setUserSubscriptionFetureId(0);
        }
    }, [data, cappedUsageFeature, isModalVisible, form]);


    const handleLimitResetChange = (value) => {
        if (value === "never") {
            form.setFieldValue("limit_value", null);
            setIsLimitValueDisabled(true);
        } else {
            form.setFieldValue("limit_value", 15);
            setIsLimitValueDisabled(false);
        }
    };

    const handleFinish = async (values) => {
        try {
            const response = await updatePlanCappedUsage(
                initialValues?.id,
                userSubscriptionId,
                values.limit_reset,
                values.limit_reset === "never" ? null : values.limit_value,
                userSubscriptionFeatureId
            );
            if (response) {
                if(response.status_code === 400){
                    notification.error({
                        message: "Update Failed",
                        description: response.message,
                    });

                    queryClient.invalidateQueries(["users"]); 
                    handleCancel();
                }
                else{
                    notification.success({ message: "Capped Usage Updated" });
                    queryClient.invalidateQueries(["users"]); 
                    handleCancel();
                }
            }
        } catch (error) {
            notification.error({
                message: "Update Failed",
                description: error.message,
            });
        }
    };

    return (
        <Modal
            title="Edit Capped Usage"
            open={isModalVisible}
            footer={null}
            onCancel={handleCancel}
        >
            {isLoading ? (
                <Spin />
            ) : data && cappedUsageFeature ? (
                <Form form={form} layout="vertical" onFinish={handleFinish}>
                    <Form.Item
                        label="Limit Reset"
                        name="limit_reset"
                        rules={[{ required: true, message: "Please select limit reset" }]}
                    >
                        <Select onChange={handleLimitResetChange}>
                            <Option value="day">Day</Option>
                            <Option value="month">Month</Option>
                            {/* <Option value="never">Never</Option> */}
                            <Option value="plan_expiry">Plan Expiry</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Limit Value"
                        name="limit_value"
                        rules={[
                            ({ getFieldValue }) => ({

                                validator(_, value) {
                                    const originalValue = getFieldValue("original_limit_value");
                                    const reset = getFieldValue("limit_reset");

                                    if (reset === "never") return Promise.resolve();
                                    if (!value || value < 1) {
                                        return Promise.reject(
                                            new Error("Limit value must be at least 1")
                                        );
                                    }
                                    if (value.toString().length > 8) {
                                        return Promise.reject(new Error("Maximum 8 digits allowed"));
                                    }

                                    if (originalValue && value < originalValue) {
                                        return Promise.reject(new Error("Limit value cannot be decreased"));
                                    }
                                    return Promise.resolve();
                                },
                            }),
                        ]}
                    >
                        <InputNumber
                            style={{ width: "100%" }}
                            min={1}
                            disabled={isLimitValueDisabled}
                            placeholder={isLimitValueDisabled ? "N/A for Never" : "Enter limit"}
                        />
                    </Form.Item>
                    
                    <Form.Item name="original_limit_value" noStyle>
                        <Input type="hidden" />
                    </Form.Item>

                    <div style={{ marginBottom: 8 }}>
                        <strong>Note:</strong> For Capped Usage – Limit value cannot be decreased. Admin can only increase the value for upgraded plans.
                    </div>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" block>
                            Update
                        </Button>
                    </Form.Item>
                </Form>
            ) : (
                <p style={{ color: "#888" }}>
                    Capped Usage feature not available for this user plan.
                </p>
            )}
        </Modal>
    );
}

export default PlanCappedModal;
